import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MailCheck } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { readLinkToken, clearLinkToken } from "@/lib/linkToken";

const PATH = "/accept-invite";

export default function AcceptInvite() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [token] = useState(() => readLinkToken(PATH));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const accept = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/onboarding/invitations/accept", { token });
      clearLinkToken(PATH);
      toast.success(`Invitation accepted (${data.role})`);
      navigate("/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not accept invitation.");
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid invitation" subtitle="This invitation link is missing or invalid.">
        <p className="text-sm text-red-700" data-testid="accept-invite-invalid">Ask the business to send a new invitation.</p>
      </AuthShell>
    );
  }
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;

  return (
    <AuthShell title="Business invitation" subtitle="Accept with the email address the invitation was sent to.">
      {!user ? (
        <div className="space-y-3 text-sm text-slate-600" data-testid="accept-invite-signin">
          <p>Sign in or create an account with the invited email address, verify it, then return here.</p>
          <div className="flex gap-3">
            <Link to="/login" className="text-[#2563EB] font-medium" data-testid="accept-invite-login-link">Sign in</Link>
            <Link to="/register" className="text-[#2563EB] font-medium" data-testid="accept-invite-register-link">Create account</Link>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Signed in as <strong data-testid="accept-invite-email">{user.email}</strong>.</p>
          {error && <p className="text-sm text-red-700" data-testid="accept-invite-error">{error}</p>}
          <button data-testid="accept-invite-btn" disabled={busy} onClick={accept}
            className="h-11 w-full rounded-md bg-[#2563EB] text-white font-medium hover:bg-[#1D4ED8] flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <MailCheck size={16} />} Accept invitation
          </button>
        </div>
      )}
    </AuthShell>
  );
}
